/**
 * Wholesale Management — Admin page
 *
 * تب‌ها:
 *  1. Overview: stats + health check
 *  2. Settings: ۳ سطح L1/L2/L3 با گروه‌بندی category
 *  3. Platform Accounts: balances و health
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  Button,
  Select,
  MenuItem,
  Switch,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  Refresh,
  Settings,
  Dashboard,
  AccountBalanceWallet,
  Save,
  HelpOutline,
} from '@mui/icons-material';
import wholesaleService, {
  WholesaleAdminSetting,
  PlatformAccountSummary,
  WholesaleStats,
  HealthcheckResponse,
} from '../services/wholesaleService';

const formatNumber = (n: number) => new Intl.NumberFormat('fa-IR').format(n);

const LEVEL_COLORS: Record<string, 'success' | 'warning' | 'error'> = {
  L1: 'success',
  L2: 'warning',
  L3: 'error',
};

const LEVEL_LABELS: Record<string, string> = {
  L1: 'ساده',
  L2: 'پیشرفته',
  L3: 'حساس',
};

const WholesaleManagement: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<WholesaleAdminSetting[]>([]);
  const [stats, setStats] = useState<WholesaleStats | null>(null);
  const [health, setHealth] = useState<HealthcheckResponse | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [settingsRes, statsRes, healthRes] = await Promise.all([
        wholesaleService.getAllSettings(),
        wholesaleService.getStats(),
        wholesaleService.healthcheck(),
      ]);
      setSettings(settingsRes);
      setStats(statsRes);
      setHealth(healthRes);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e.message ?? 'خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleSettingChange = (key: string, value: any) => {
    setEditValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSettingSave = async (s: WholesaleAdminSetting) => {
    setSaving((prev) => ({ ...prev, [s.key]: true }));
    try {
      const newValue = editValues[s.key] ?? s.value;
      await wholesaleService.updateSetting(s.key, newValue);
      setSuccess(`${s.displayName} به‌روزرسانی شد`);
      setEditValues((prev) => {
        const next = { ...prev };
        delete next[s.key];
        return next;
      });
      await loadAll();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.reason ?? 'خطا در ذخیره');
    } finally {
      setSaving((prev) => ({ ...prev, [s.key]: false }));
    }
  };

  // گروه‌بندی settings بر اساس category
  const groupedSettings = useMemo(() => {
    const groups: Record<string, WholesaleAdminSetting[]> = {};
    for (const s of settings) {
      if (!groups[s.category]) groups[s.category] = [];
      groups[s.category].push(s);
    }
    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => a.displayOrder - b.displayOrder)
    );
    return groups;
  }, [settings]);

  const renderSettingInput = (s: WholesaleAdminSetting) => {
    const currentValue = editValues[s.key] ?? s.value;
    const isEdited = editValues[s.key] !== undefined;
    const isSaving = saving[s.key];

    let input;
    switch (s.valueType) {
      case 'boolean':
        input = (
          <Switch
            checked={!!currentValue}
            onChange={(e) => handleSettingChange(s.key, e.target.checked)}
          />
        );
        break;
      case 'number':
        input = (
          <TextField
            type="number"
            size="small"
            value={currentValue ?? ''}
            inputProps={{ min: s.minValue, max: s.maxValue }}
            onChange={(e) => handleSettingChange(s.key, parseFloat(e.target.value))}
            sx={{ width: 180 }}
          />
        );
        break;
      case 'enum':
        input = (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <Select
              value={currentValue ?? ''}
              onChange={(e) => handleSettingChange(s.key, e.target.value)}
            >
              {(s.enumValues ?? []).map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
        break;
      case 'json':
        input = (
          <TextField
            multiline
            rows={3}
            size="small"
            value={JSON.stringify(currentValue, null, 2)}
            onChange={(e) => {
              try {
                handleSettingChange(s.key, JSON.parse(e.target.value));
              } catch {
                // ignore parse error during typing
              }
            }}
            sx={{ width: 360 }}
          />
        );
        break;
      default:
        input = (
          <TextField
            size="small"
            value={currentValue ?? ''}
            onChange={(e) => handleSettingChange(s.key, e.target.value)}
            sx={{ width: 250 }}
          />
        );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {input}
        {isEdited && (
          <Button
            size="small"
            variant="contained"
            startIcon={<Save />}
            onClick={() => handleSettingSave(s)}
            disabled={isSaving}
          >
            ذخیره
          </Button>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Container sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          مدیریت عمده‌فروشی
        </Typography>
        <IconButton onClick={loadAll}>
          <Refresh />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab icon={<Dashboard />} label="نمای کلی" />
          <Tab icon={<Settings />} label="تنظیمات" />
          <Tab icon={<AccountBalanceWallet />} label="حساب‌های پلتفرم" />
        </Tabs>
      </Paper>

      {/* ─── Overview Tab ─── */}
      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  استخرهای فعال
                </Typography>
                <Typography variant="h4">
                  {stats ? formatNumber(stats.pools.active) : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  از کل {stats ? formatNumber(stats.pools.total) : 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  آیتم‌های موجود
                </Typography>
                <Typography variant="h4">
                  {stats ? formatNumber(stats.items.available) : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  از کل {stats ? formatNumber(stats.items.total) : 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  فروش‌های موفق
                </Typography>
                <Typography variant="h4">
                  {stats ? formatNumber(stats.items.sold) : '-'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  ریسلرها
                </Typography>
                <Typography variant="h4">
                  {stats ? formatNumber(stats.resellers) : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats ? formatNumber(stats.listings) : 0} listing فعال
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  وضعیت سیستم
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={
                      health?.enabled
                        ? '✅ سیستم wholesale فعال'
                        : '⛔ سیستم wholesale غیرفعال'
                    }
                    color={health?.enabled ? 'success' : 'default'}
                  />
                  <Chip
                    label={`${health?.adminSettings ?? 0} تنظیمات admin`}
                    color="info"
                  />
                  <Chip
                    label={`${health?.platformAccounts?.length ?? 0} حساب پلتفرم`}
                    color="info"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ─── Settings Tab ─── */}
      {tab === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            تنظیمات در ۳ سطح هستن: <strong>L1 ساده</strong>،{' '}
            <strong>L2 پیشرفته</strong>، <strong>L3 حساس</strong> (نیاز به super admin).
            هر تغییر در audit log ثبت می‌شه.
          </Alert>
          {Object.entries(groupedSettings).map(([category, items]) => (
            <Paper key={category} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {category}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {items.map((s) => (
                <Box
                  key={s.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1.5,
                    borderBottom: '1px solid #f0f0f0',
                    gap: 2,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {s.displayName}
                      </Typography>
                      <Chip
                        label={LEVEL_LABELS[s.level]}
                        size="small"
                        color={LEVEL_COLORS[s.level]}
                      />
                      {s.description && (
                        <Tooltip title={s.description}>
                          <HelpOutline fontSize="small" color="action" />
                        </Tooltip>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {s.key}
                    </Typography>
                  </Box>
                  {renderSettingInput(s)}
                </Box>
              ))}
            </Paper>
          ))}
        </Box>
      )}

      {/* ─── Platform Accounts Tab ─── */}
      {tab === 2 && (
        <Grid container spacing={2}>
          {health?.platformAccounts?.map((acc) => (
            <Grid item xs={12} md={6} key={acc._id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="h6">{acc.displayName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {acc.accountType}
                      </Typography>
                    </Box>
                    <Chip
                      label={acc.isActive ? 'فعال' : 'غیرفعال'}
                      size="small"
                      color={acc.isActive ? 'success' : 'default'}
                    />
                  </Box>
                  <Typography variant="h4" sx={{ mt: 2 }}>
                    {formatNumber(Math.round(acc.balance))}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}تومان
                    </Typography>
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default WholesaleManagement;
